import React from "react";
import { Box } from "@mui/material";

/**
 * Flx — thin wrapper around MUI Box that defaults to display: flex with shorthand
 * props. Use this instead of <Box sx={{ display: 'flex', flexDirection: ... }}>
 * for everyday flexbox layout. The sx prop is still passed through, so any extra
 * styling can override the defaults.
 *
 * Props:
 *   column / row — flex direction shortcut (row is default)
 *   gap — number passed to MUI spacing scale (e.g. gap={2} → theme.spacing(2))
 *   centerY — alignItems: center
 *   centerX — justifyContent: center
 *   between — justifyContent: space-between
 *   end — justifyContent: flex-end
 *   wrap — flexWrap: wrap
 *   inline — display: inline-flex
 *   fullWidth — width: 100%
 *   fullHeight — height: 100%
 */
const Flx = React.forwardRef(function Flx(
  {
    column = false,
    row,
    gap,
    centerY = false,
    centerX = false,
    between = false,
    end = false,
    wrap = false,
    inline = false,
    fullWidth = false,
    fullHeight = false,
    sx,
    children,
    ...rest
  },
  ref
) {
  const direction = column ? "column" : "row";
  const justify = between ? "space-between" : end ? "flex-end" : centerX ? "center" : undefined;
  const align = centerY ? "center" : undefined;

  return (
    <Box
      ref={ref}
      sx={{
        display: inline ? "inline-flex" : "flex",
        flexDirection: direction,
        ...(gap !== undefined ? { gap } : {}),
        ...(justify ? { justifyContent: justify } : {}),
        ...(align ? { alignItems: align } : {}),
        ...(wrap ? { flexWrap: "wrap" } : {}),
        ...(fullWidth ? { width: "100%" } : {}),
        ...(fullHeight ? { height: "100%" } : {}),
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
});

export default Flx;
